import React from "react";
import { useLocation } from "react-router-dom";
import "./SiteFooter.css";
import {
    FOOTER_GROUPS,
    routesInGroup,
    footerGroupOf,
    isToolRoute,
    findBySlug,
    indexRoute,
    localeOf,
    contentRoutes,
    SITE_NAME,
} from "./Seo/routeSeo.ts";
import { useTranslation } from "./i18n/useTranslation.ts";

/**
 * The footer, and with it most of the site's internal link graph.
 *
 * It used to link every page from every page. That was the right fix when the
 * side nav navigated by assigning to window.location and the landing pages had
 * no crawlable links at all, but at 84 generators the footer had grown longer
 * than the pages carrying it - 297 words and 85 links against about 250 words
 * of actual content - and a graph where everything links everything says
 * nothing about which pages matter.
 *
 * So: each page links its own group in full, plus /all-worksheets, which lists
 * everything. Every page stays two clicks from every other, sibling links are
 * topical rather than exhaustive, and the index becomes the hub. See ADR 0027.
 *
 * scripts/prerender.js writes the same markup into the static HTML - change
 * both, and prerender.test.js checks that they agree.
 */
export function SiteFooter() {
    const { pathname } = useLocation();
    const current = findBySlug(pathname);
    // ADR 0051 §8: a page's own locale decides which index and which
    // siblings it offers - a page not in the manifest (404) defaults to
    // English, matching what it always meant before locale existed.
    const locale = current ? localeOf(current) : undefined;
    const index = indexRoute(locale);
    const t = useTranslation();

    // Pages outside a generator group - About, Privacy, the index itself, 404 -
    // have no siblings to offer, so they lead with the index instead.
    const groupKey = current && isToolRoute(current) ? footerGroupOf(current) : null;
    const siblings = groupKey ? routesInGroup(groupKey, locale) : [];
    const heading = FOOTER_GROUPS.find((group) => group.key === groupKey)?.heading;

    return (
        <footer className="siteFooter">
            <nav className="footerLinks" aria-label={t('footer.moreFromThisSiteAria')}>
                {siblings.length > 0 && (
                    <div className="footerColumn">
                        {/*
                          * Not an <h2>. Fourteen footer headings on every page
                          * buried the one heading that belonged to the page; the
                          * nav is already a labelled landmark and needs none.
                          */}
                        <p className="footerColumnHeading">{heading}</p>
                        <ul>
                            {siblings.map((route) => (
                                <li key={route.slug}>
                                    <a href={route.slug}>{route.h1}</a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {index && (
                    <p className="footerIndexLink">
                        <a href={index.slug}>{index.h1}</a>
                    </p>
                )}
            </nav>

            {/*
              * About and Privacy are not peers of the generators, and a privacy
              * policy has to be reachable from every page. Its own row, not a
              * column. prerender.js carries a copy; change both.
              */}
            <nav className="footerLegal" aria-label={t('footer.aboutThisSiteAria')}>
                <ul>
                    {contentRoutes().map((route) => (
                        <li key={route.slug}>
                            <a href={route.slug}>{route.h1}</a>
                        </li>
                    ))}
                </ul>
            </nav>
            <p className="footerNote">
                <a href="/">{SITE_NAME}</a>{t('footer.tagline')}
            </p>
        </footer>
    );
}

export default SiteFooter;
